/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { signIn } from "next-auth/react";
import toast from "react-hot-toast";
import { SeaaiiAuthFrame, SaCardLogo, SaQrPanel, SaEye, SaIcons } from "./SeaaiiAuthFrame";
import SeaaiiDemoBooking from "./SeaaiiDemoBooking";

type Step = "form" | "otp" | "booking" | "pending";

// Seaaii sign-up. Leads land under the Seaaii partner with PENDING approval
// (set server-side in /api/auth/register from the host); after OTP we show a
// "we'll call you" screen instead of logging in. The design's extra fields —
// shop name + discount code — are collected in the UI but not yet wired to the
// backend (the register API only accepts name/email/phone/password).
export default function SeaaiiSignupPage() {
  const router = useRouter();

  const inviteToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("invite") : null;
  const callbackUrl = inviteToken
    ? `/invite/${inviteToken}`
    : typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("callbackUrl") || "/dashboard"
      : "/dashboard";

  const [step, setStep] = useState<Step>("form");
  const [loading, setLoading] = useState(false);
  const [refno, setRefno] = useState("");
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [userId, setUserId] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const [form, setForm] = useState({
    shopName: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    discountCode: "",
  });
  const change = (e: React.ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);
  useEffect(() => {
    if (otpExpiry <= 0) return;
    const t = setTimeout(() => setOtpExpiry((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpExpiry]);

  const fmtExpiry = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  const fmtPhone = (p: string) => (p.length === 10 ? `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}` : p);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) return toast.error("กรุณากรอกข้อมูลให้ครบถ้วน");
    if (!/^0[0-9]{9}$/.test(form.phone)) return toast.error("กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง (10 หลัก)");
    if (form.password.length < 8) return toast.error("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
    if (!agreed) return toast.error("กรุณายอมรับเงื่อนไขการใช้งาน");

    setLoading(true);
    try {
      // Contact name → `name`; shop name + the sale referral code (the
      // "รหัสส่วนลด" field) are now wired through. A valid code attributes
      // the lead to a sale + grants a permanent 3% package discount.
      const registerRes = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
          shopName: form.shopName || undefined,
          referralCode: form.discountCode || undefined,
          inviteToken: inviteToken || undefined,
        }),
      });
      const registerData = await registerRes.json();
      if (!registerRes.ok) throw new Error(registerData.error || "เกิดข้อผิดพลาด");
      setPendingApproval(registerData.pendingApproval === true);
      setUserId(registerData.user?.id || "");

      const otpRes = await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.phone, inviteToken: inviteToken || undefined }),
      });
      const otpData = await otpRes.json();
      if (!otpRes.ok) throw new Error(otpData.error || "ไม่สามารถส่ง OTP ได้");

      setRefno(otpData.refno);
      setStep("otp");
      setResendCooldown(60);
      setOtpExpiry(30 * 60);
      toast.success(`ส่ง OTP ไปยัง ${form.phone} แล้ว (Ref: ${otpData.refno})`);
      setTimeout(() => pinRefs.current[0]?.focus(), 120);
    } catch (err: any) {
      toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pinStr = pin.join("");
    if (pinStr.length < 6) return toast.error("กรุณากรอก OTP ให้ครบ 6 หลัก");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.phone, pin: pinStr }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "รหัส OTP ไม่ถูกต้อง");
      toast.success("ยืนยันเบอร์โทรศัพท์สำเร็จ!");

      // Seaaii leads → offer the Demo booking screen (skip → pending
      // thank-you). Fall back to pending if we somehow lack the userId.
      if (pendingApproval) { setStep(userId ? "booking" : "pending"); return; }

      const result = await signIn("credentials", { email: form.email, password: form.password, redirect: false });
      if (result?.ok) router.push(callbackUrl);
    } catch (err: any) {
      toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "ไม่สามารถส่ง OTP ได้");
      setRefno(data.refno);
      setPin(["", "", "", "", "", ""]);
      setResendCooldown(60);
      setOtpExpiry(30 * 60);
      toast.success(`ส่ง OTP ใหม่แล้ว (Ref: ${data.refno})`);
      setTimeout(() => pinRefs.current[0]?.focus(), 120);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onPin = (i: number, v: string) => {
    if (!/^[0-9]?$/.test(v)) return;
    const next = [...pin]; next[i] = v; setPin(next);
    if (v && i < 5) pinRefs.current[i + 1]?.focus();
  };
  const onPinKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !pin[i] && i > 0) pinRefs.current[i - 1]?.focus();
  };
  const onPinPaste = (e: React.ClipboardEvent) => {
    const d = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (d.length === 6) { setPin(d.split("")); pinRefs.current[5]?.focus(); }
  };

  // The Demo-booking screen is its own full-page layout (wider card, own
  // header/bg), so it renders outside the auth frame.
  if (step === "booking") {
    return <SeaaiiDemoBooking userId={userId} onSkip={() => setStep("pending")} />;
  }

  return (
    <SeaaiiAuthFrame alignEnd={step === "form"}>
      {step === "form" && (
        <div className="auth-wrap">
          <div className="views">
            <section className="card" style={{ width: 560, maxWidth: "100%" }}>
              <h1 className="card-title">สมัครสมาชิก</h1>

              <form className="reg-form" onSubmit={handleFormSubmit}>
                <Row label="ชื่อร้านค้า / บริษัท" icon={SaIcons.store} name="shopName" value={form.shopName} onChange={change} placeholder="กรอกชื่อร้านค้า หรือชื่อบริษัท" />
                <Row label="ชื่อผู้ติดต่อ" icon={SaIcons.user} name="name" value={form.name} onChange={change} placeholder="กรอกชื่อ-นามสกุลผู้ติดต่อ" required />
                <Row label="อีเมล" icon={SaIcons.mail} name="email" type="email" value={form.email} onChange={change} placeholder="กรอกอีเมลของคุณ" required />
                <Row label="เบอร์โทรศัพท์" icon={SaIcons.phone} name="phone" type="tel" maxLength={10} value={form.phone} onChange={change} placeholder="กรอกเบอร์โทรศัพท์ของคุณ" required />
                <div className="form-row">
                  <label>รหัสผ่าน</label>
                  <div className="input">
                    <span className="lead">{SaIcons.lock}</span>
                    <input name="password" type={showPw ? "text" : "password"} value={form.password} onChange={change} required placeholder="ตั้งรหัสผ่าน (อย่างน้อย 8 ตัวอักษร)" />
                    <SaEye shown={showPw} onToggle={() => setShowPw((s) => !s)} />
                  </div>
                </div>
                <Row label="รหัสส่วนลด" icon={SaIcons.tag} name="discountCode" value={form.discountCode} onChange={change} placeholder="กรอกรหัสส่วนลด (ถ้ามี)" />

                <label className="check-row">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  <span>ฉันยอมรับ <Link href="/terms" target="_blank">เงื่อนไขการใช้งาน</Link> และ <Link href="/terms" target="_blank">นโยบายความเป็นส่วนตัว</Link></span>
                </label>

                <button className="btn-primary" type="submit" disabled={loading} style={{ marginTop: 6 }}>
                  {loading ? "กำลังสมัครสมาชิก..." : "สมัครสมาชิก"}
                </button>

                <p className="switch-line">
                  มีบัญชีอยู่แล้ว? <Link href={`/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`}>เข้าสู่ระบบ</Link>
                </p>
              </form>
            </section>
          </div>
          <SaQrPanel />
        </div>
      )}

      {step === "otp" && (
        <section className="card" style={{ maxWidth: 480 }}>
          <SaCardLogo />
          <h1 className="card-title">ยืนยันเบอร์โทรศัพท์</h1>
          <p className="card-sub">
            ส่ง OTP ไปยัง <b className="mono">{fmtPhone(form.phone)}</b>{" "}
            {refno && <span className="ref">(Ref: {refno})</span>}
          </p>

          <form className="form" onSubmit={handleOtpSubmit}>
            <div className="field">
              <label className="center">กรอกรหัส OTP 6 หลัก</label>
              <div className="otp-row" onPaste={onPinPaste}>
                {pin.map((d, i) => (
                  <input key={i} ref={(el) => { pinRefs.current[i] = el; }} type="text" inputMode="numeric" maxLength={1}
                    className={d ? "filled" : ""} value={d} onChange={(e) => onPin(i, e.target.value)} onKeyDown={(e) => onPinKey(i, e)} />
                ))}
              </div>
              <div className={`otp-timer ${otpExpiry > 0 && otpExpiry > 60 ? "ok" : "warn"}`}>
                {otpExpiry > 0 ? `OTP หมดอายุใน ${fmtExpiry(otpExpiry)}` : "OTP หมดอายุแล้ว กรุณาขอใหม่"}
              </div>
            </div>

            <button className="btn-primary" type="submit" disabled={loading || pin.join("").length < 6 || otpExpiry === 0}>
              {loading ? "กำลังยืนยัน..." : "ยืนยัน OTP"}
            </button>

            <div className="row-between">
              <button type="button" className="linkbtn" onClick={() => { setStep("form"); setPin(["", "", "", "", "", ""]); }}>← แก้ไขข้อมูล</button>
              <button type="button" className="linkbtn blue" onClick={handleResend} disabled={resendCooldown > 0 || loading}>
                {resendCooldown > 0 ? `ส่งใหม่ใน ${resendCooldown}s` : "ส่ง OTP ใหม่"}
              </button>
            </div>
          </form>
        </section>
      )}

      {step === "pending" && (
        <section className="card" style={{ maxWidth: 480 }}>
          <SaCardLogo />
          <h1 className="card-title">ขอบคุณที่ให้ความสนใจ</h1>
          <p className="card-sub">
            เราได้รับข้อมูลการสมัครของคุณแล้ว ทีมงานจะติดต่อกลับไปที่เบอร์{" "}
            <b className="mono">{fmtPhone(form.phone)}</b> ภายใน 24 ชั่วโมง
          </p>
          <div className="status green mt20">
            <div className="ic"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div>
            <h3>บัญชีอยู่ระหว่างรอการอนุมัติ</h3>
            <p>คุณจะเข้าสู่ระบบได้หลังทีมงานยืนยันบัญชีของคุณ</p>
          </div>
          <a className="back-link mt20" href="https://seaaii.com" style={{ display: "inline-flex", width: "100%", justifyContent: "center" }}>← กลับหน้าแรก</a>
        </section>
      )}
    </SeaaiiAuthFrame>
  );
}

function Row({ label, icon, ...props }: { label: string; icon: React.ReactNode } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="form-row">
      <label>{label}</label>
      <div className="input">
        <span className="lead">{icon}</span>
        <input {...props} />
      </div>
    </div>
  );
}
